import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnswerValue, CanonicalQuestion, UserAnswer } from '../../types';
import { formatRelative } from '../../utils/format';

interface Props {
  question: CanonicalQuestion;
  answer?: UserAnswer;
  /** Outlines the row, for one the page has just scrolled to. */
  highlighted?: boolean;
  onSave: (value: AnswerValue, verified: boolean) => Promise<void>;
  onClear: () => Promise<void>;
}

const isEmpty = (v: AnswerValue) => v === null || v === undefined || v === '';

/** The DOM id of a question's row, for scrolling to it. */
export const answerRowId = (questionKey: string): string => `answer-${questionKey.replace(/\W/g, '-')}`;

const QuestionAnswerRow: React.FC<Props> = ({ question, answer, highlighted = false, onSave, onClear }) => {
  const serverValue: AnswerValue = answer ? answer.value : null;
  const serverVerified = answer ? Boolean(answer.verified) : false;

  const [value, setValue] = useState<AnswerValue>(serverValue);
  const [verified, setVerified] = useState<boolean>(serverVerified);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Resync when the server copy changes (after save/clear or reload).
  useEffect(() => {
    setValue(serverValue);
    setVerified(serverVerified);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer?.value, answer?.verified, answer?.updated_at]);

  const dirty = useMemo(
    () => value !== serverValue || verified !== serverVerified,
    [value, verified, serverValue, serverVerified]
  );

  const canSave = dirty && !isEmpty(value) && !saving && !clearing;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(value, verified);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await onClear();
    } finally {
      setClearing(false);
    }
  };

  const inputId = `q-${question.key.replace(/\W/g, '-')}`;
  const disabled = saving || clearing;

  const renderInput = () => {
    switch (question.value_type) {
      case 'boolean': {
        const radioValue = value === true ? 'yes' : value === false ? 'no' : 'unanswered';
        return (
          <RadioGroup
            row
            value={radioValue}
            onChange={(e) => {
              const v = e.target.value;
              setValue(v === 'yes' ? true : v === 'no' ? false : null);
            }}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" disabled={disabled} />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" disabled={disabled} />
            <FormControlLabel
              value="unanswered"
              control={<Radio size="small" />}
              label="Unanswered"
              disabled={disabled}
            />
          </RadioGroup>
        );
      }
      case 'enum':
        return (
          <FormControl size="small" fullWidth sx={{ maxWidth: 320 }}>
            <InputLabel id={`${inputId}-label`}>Choose</InputLabel>
            <Select
              labelId={`${inputId}-label`}
              label="Choose"
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setValue(e.target.value === '' ? null : e.target.value)}
              disabled={disabled}
            >
              <MenuItem value="">
                <em>Unanswered</em>
              </MenuItem>
              {(question.options ?? []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'long_text':
        return (
          <TextField
            value={typeof value === 'string' ? value : value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            size="small"
            disabled={disabled}
            placeholder="Your answer"
          />
        );
      case 'number':
        return (
          <TextField
            type="number"
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(e) => {
              const raw = e.target.value;
              setValue(raw === '' ? null : Number(raw));
            }}
            size="small"
            sx={{ maxWidth: 240 }}
            disabled={disabled}
          />
        );
      case 'money':
        return (
          <TextField
            value={typeof value === 'string' ? value : value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            size="small"
            sx={{ maxWidth: 320 }}
            disabled={disabled}
            placeholder="e.g. 120,000 USD"
          />
        );
      case 'date':
        return (
          <TextField
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => setValue(e.target.value === '' ? null : e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 240 }}
            disabled={disabled}
          />
        );
      default:
        return (
          <TextField
            value={typeof value === 'string' ? value : value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: 480 }}
            disabled={disabled}
            placeholder="Your answer"
          />
        );
    }
  };

  return (
    <Box
      id={answerRowId(question.key)}
      sx={{
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
        ...(highlighted && { outline: 2, outlineColor: 'primary.main', outlineOffset: -2, borderRadius: 1, px: 1, mx: -1 }),
        transition: 'outline-color 0.3s',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={600} component="label" htmlFor={inputId}>
              {question.label}
            </Typography>
            {answer ? (
              <Chip
                label={answer.verified ? 'Verified' : 'Saved, not verified'}
                size="small"
                color={answer.verified ? 'success' : 'default'}
                variant="outlined"
              />
            ) : (
              <Chip label="Not answered" size="small" variant="outlined" />
            )}
          </Stack>
          {question.help_text && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {question.help_text}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>{renderInput()}</Box>
          {answer && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Updated {formatRelative(answer.updated_at)}
              {answer.use_count ? ` | used ${answer.use_count} time${answer.use_count === 1 ? '' : 's'}` : ''}
            </Typography>
          )}
        </Box>

        <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }} sx={{ minWidth: 180 }}>
          <Tooltip title="Only verified answers are filled automatically. Unverified answers are suggested, never submitted for you.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                  disabled={disabled}
                />
              }
              label={<Typography variant="body2">Verified</Typography>}
              sx={{ mr: 0 }}
            />
          </Tooltip>
          <Stack direction="row" spacing={1}>
            {answer && (
              <Button size="small" color="inherit" onClick={handleClear} disabled={disabled}>
                {clearing ? <CircularProgress size={16} color="inherit" /> : 'Clear'}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleSave} disabled={!canSave}>
              {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default QuestionAnswerRow;
